import { Box, Skeleton, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CustomButton } from "src/design";
import {
  colorAccent,
  colorActionPrimary,
  colorActionPrimaryText,
  colorBorder,
  colorSurface,
  colorTextDisabled,
  colorTextMuted,
  radiusCard,
  radiusChip,
  shadowCard,
} from "src/design/tokens";
import { ServiceHour, findQuietWindow } from "../utils/serviceOccupancy";

const BAR_HEIGHT = 72;

interface QuietSlotsCardProps {
  restaurantId: string;
  hours: ServiceHour[];
  isLoading: boolean;
  /** True when the restaurant is closed today — there is no service to fill. */
  isClosed: boolean;
}

const formatHour = (hour: number): string =>
  `${String(hour).padStart(2, "0")}:00`;

const HourBar = ({ entry }: { entry: ServiceHour }) => (
  <Box sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
    <Box
      sx={{
        height: BAR_HEIGHT,
        borderRadius: radiusChip,
        display: "flex",
        flexDirection: "column-reverse",
        overflow: "hidden",
        backgroundColor: "rgba(255,255,255,0.12)",
        border: entry.quiet
          ? "1.5px dashed rgba(255,255,255,0.45)"
          : "1.5px solid transparent",
      }}
    >
      <Box
        sx={{
          height: `${Math.round(entry.fill * 100)}%`,
          backgroundColor: colorAccent,
          borderRadius: `${radiusChip} ${radiusChip} 0 0`,
        }}
      />
    </Box>
    <Typography
      sx={{
        fontSize: "10.5px",
        mt: 0.75,
        color: entry.quiet ? colorActionPrimaryText : colorTextDisabled,
        fontWeight: entry.quiet ? 600 : 400,
      }}
    >
      {`${entry.hour}h`}
    </Typography>
  </Box>
);

/**
 * Occupancy across today's service, highlighting the stretch that is running
 * empty so the restaurateur can push an offer into it.
 */
export const QuietSlotsCard = ({
  restaurantId,
  hours,
  isLoading,
  isClosed,
}: QuietSlotsCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const quietWindow = findQuietWindow(hours);
  // Nothing booked at all: there is no stretch to point at, so the card pitches
  // the whole service instead of a window inside it.
  const isEmptyService =
    hours.length > 0 && hours.every((entry) => entry.tables === 0);
  const callout = isEmptyService
    ? t("dashboard.quietSlots.emptyService")
    : quietWindow
      ? t("dashboard.quietSlots.callout", {
          start: formatHour(quietWindow.startHour),
          end: formatHour(quietWindow.endHour),
        })
      : null;

  return (
    <Box
      sx={{
        height: "100%",
        backgroundColor: colorActionPrimary,
        border: `1px solid ${colorBorder}`,
        borderRadius: radiusCard,
        boxShadow: shadowCard,
        px: 3,
        py: 2.5,
      }}
    >
      <Typography
        sx={{
          fontSize: "16px",
          fontWeight: 600,
          color: colorActionPrimaryText,
        }}
      >
        {t("dashboard.quietSlots.title")}
      </Typography>
      <Typography sx={{ fontSize: "12.5px", color: colorTextDisabled, mb: 2 }}>
        {t("dashboard.quietSlots.subtitle")}
      </Typography>

      {isLoading ? (
        <Skeleton
          variant="rounded"
          height={BAR_HEIGHT + 20}
          sx={{ backgroundColor: "rgba(255,255,255,0.12)" }}
        />
      ) : hours.length === 0 ? (
        <Typography sx={{ fontSize: "13px", color: colorTextDisabled, py: 3 }}>
          {isClosed
            ? t("dashboard.quietSlots.closed")
            : t("dashboard.quietSlots.noSchedule")}
        </Typography>
      ) : (
        <Box sx={{ display: "flex", gap: 0.75 }}>
          {hours.map((entry) => (
            <HourBar key={entry.hour} entry={entry} />
          ))}
        </Box>
      )}

      {!isLoading && callout && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
            mt: 2,
            px: 2,
            py: 1.5,
            borderRadius: radiusChip,
            backgroundColor: "rgba(255,255,255,0.10)",
          }}
        >
          <Typography
            sx={{ fontSize: "13px", color: colorSurface, flex: 1, minWidth: 180 }}
          >
            {callout}
          </Typography>
          <CustomButton
            color="black"
            onClick={() => navigate(`/restaurant/${restaurantId}/deals`)}
            sx={{
              backgroundColor: colorAccent,
              color: colorActionPrimary,
              "&:hover": { backgroundColor: colorAccent, opacity: 0.9 },
            }}
          >
            {t("dashboard.quietSlots.createOffer")}
          </CustomButton>
        </Box>
      )}

      {!isLoading && hours.length > 0 && !callout && (
        <Typography sx={{ fontSize: "13px", color: colorTextMuted, mt: 2 }}>
          {t("dashboard.quietSlots.noQuietWindow")}
        </Typography>
      )}
    </Box>
  );
};
